// Carte d'intégration Microsoft Outlook (EPIC Outlook PR1).
import { useState } from 'react';
import { toast } from 'sonner';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import { Card } from '../ui/Card';
import {
  useOutlookConnection,
  useDisconnectOutlook,
} from '../../hooks/queries/useOutlookConnection';
import { startOutlookConnect } from '../../services/outlook/outlookConnectionService';
import { OutlookTriageQueue } from './OutlookTriageQueue';

export function OutlookIntegrationCard() {
  const { data: conn, isLoading, isError } = useOutlookConnection();
  const disconnect = useDisconnectOutlook();
  const [connecting, setConnecting] = useState(false);
  const [params] = useSearchParams();
  const justConnected = params.get('outlook') === 'connected';

  const onConnect = async () => {
    setConnecting(true);
    try {
      await startOutlookConnect();
    } catch (err) {
      setConnecting(false);
      toast.error(err instanceof Error ? err.message : 'Échec de la connexion Outlook');
    }
  };

  return (
    <Card
      header={
        <div className="flex items-center gap-2">
          <img src="/icons/microsoft.svg" alt="Microsoft" className="w-4 h-4" />
          <span className="text-sm font-semibold text-neutral-700">
            Microsoft Outlook
          </span>
        </div>
      }
    >
      <p className="text-sm text-neutral-500 mb-4">
        Connectez votre compte Microsoft 365 pour synchroniser vos e-mails et pièces
        jointes avec vos dossiers.
      </p>

      {isLoading ? (
        <Loader2 className="w-4 h-4 animate-spin text-neutral-400" aria-hidden />
      ) : isError ? (
        <p className="text-sm text-red-600">
          Impossible de vérifier la connexion. Réessayez plus tard.
        </p>
      ) : conn && conn.status === 'active' ? (
        <div className="space-y-2">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm text-neutral-700">
                Connecté : <strong>{conn.ms_email}</strong>
              </p>
              {conn.last_sync_at && (
                <p className="text-xs text-neutral-400 mt-0.5">
                  Dernière sync :{' '}
                  {new Date(conn.last_sync_at).toLocaleString('fr-FR')}
                </p>
              )}
            </div>
            <button
              onClick={() => disconnect.mutate()}
              disabled={disconnect.isPending}
              className="text-xs text-red-600 hover:underline disabled:opacity-50 shrink-0 mt-0.5"
            >
              {disconnect.isPending ? 'Déconnexion…' : 'Déconnecter'}
            </button>
          </div>
          {justConnected && (
            <p className="text-sm text-green-600">Compte Outlook connecté.</p>
          )}
        </div>
      ) : conn && conn.status === 'revoked' ? (
        <div className="space-y-2">
          <p className="flex items-center gap-2 text-sm text-amber-700">
            <AlertTriangle className="w-4 h-4" aria-hidden />
            Accès Outlook révoqué.
            {conn.last_error && (
              <span className="text-xs text-neutral-400">({conn.last_error})</span>
            )}
          </p>
          <button
            onClick={onConnect}
            disabled={connecting}
            className="text-sm font-medium text-cime-primary hover:underline disabled:opacity-50"
          >
            Reconnecter
          </button>
        </div>
      ) : (
        // status 'pending' (connexion entamée mais non finalisée) ou pas de connexion
        // → on propose simplement de (re)lancer la connexion, ce n'est pas une erreur.
        <button
          onClick={onConnect}
          disabled={connecting}
          className="rounded-md bg-cime-primary px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
        >
          {connecting ? 'Redirection…' : 'Connecter Outlook'}
        </button>
      )}

      <div className="mt-6 border-t border-neutral-100 pt-4">
        <p className="mb-2 text-sm font-semibold text-neutral-700">Mails à trier</p>
        <OutlookTriageQueue />
      </div>
    </Card>
  );
}
